import { EditPropertyContainer } from 'components/order/overview/EditProperty/EditPropertyContainer';
import { ActivityLogPage } from 'pages/order/ActivityLogPage';
import { OrderLayout } from 'components/order/OrderLayout';
import { TitleContainer } from 'pages/order/title/TitleContainer';
import { OrderContactsContainer } from 'pages/order/contacts/OrderContactsContainer';
import { OrderOverviewPage } from 'pages/order/OrderOverviewPage';
import React from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import { routes } from '../../utilities/order/formConfig';
import { EditTransactionRoute } from '../../components/order/overview/EditTransactionPrice/EditTransactionRoute';
import { TaxCertificationContainer } from '../../pages/order/taxCertifications/TaxCertificationContainer';
import { selectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { DocumentsPage } from 'pages/order/documentsPortal/DocumentsPage';
import { DocumentHistoryContainer } from '../../pages/order/documentsPortal/DocumentHistoryContainer';
import { SpaOrderEscrowViewContainer } from 'pages/order/escrow/spa/SpaOrderEscrowViewContainer';
import { SpaOrderEscrowCreateEditContainer } from '../../pages/order/escrow/spa/SpaOrderEscrowCreateEditContainer';
import { FeesPage } from 'pages/fees/FeesPage';
import { OrderAdministrationPage } from 'pages/order/OrderAdministrationPage';
import { SchedulingContainer } from '../../pages/order/scheduling/SchedulingContainer';
import { ApplicationProvider } from 'state/orderOverview/controls/ApplicationProvider';
import { OrderLoadingGuard } from '../../store/order/orderData/OrderLoadingGuard';
import { UUID } from '../../types/shared/UUID';
import { useRouteMatch } from 'react-router';
import { CDBuilderPage } from '../../components/cdBuilder/CdBuilderPage';
import { useSelector } from 'react-redux';
import { CommentsRouter } from './CommentsRouter';
import { TrackOrderVisit } from 'components/shared/SearchBar/TrackOrderVisit';
import { DisburseAllContainer } from 'components/order/fees/disburseAll/DisburseAllContainer';
import PolicyRouter from './PolicyRouter';
import { TitleReportRouter } from '../TitleReportRouter';
import { WorkflowContainer } from 'pages/order/workflow/WorkflowContainer';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';
import { TasksPage } from 'pages/tasks/TasksPage';
import { InboxPage } from '../../pages/inbox/InboxPage';

export const AdminOrderRouter: React.FC = () => {
  const { path } = useRouteMatch();
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data, fetchStatus } = useSelector(selectCurrentOrder);

  const isOrderWorkflowEnabled = useHasFeatureFlag('order.workflow');
  const isMessagingEnabled = useHasFeatureFlag('messaging.ui');
  // Current pages assume that the order is loaded.
  // Rather than break them all in this refactor, we're keeping this assumption.
  return (
    <OrderLoadingGuard orderId={orderId}>
      <ApplicationProvider>
        <TrackOrderVisit fileNumber={data?.transaction.file_number} />
        <Switch>
          <Route path={`${path}/property`} component={EditPropertyContainer} />
          <Route path={`${path}/taxCertification`} component={TaxCertificationContainer} />
          <Route path={`${path}/transaction/:field(${routes.join('|')})`} exact component={EditTransactionRoute} />
          <Route path={`${path}/disburse-all`} component={DisburseAllContainer} />
          <Route path={`${path}/policy`} component={PolicyRouter} />
          <Route path={`${path}/report`} component={TitleReportRouter} />
          <OrderLayout fetchStatus={fetchStatus}>
            <Route path={path} exact component={OrderOverviewPage} />
            <Route path={`${path}/activity`} exact component={ActivityLogPage} />
            <Route path={`${path}/administration`} component={OrderAdministrationPage} />
            <Route path={`${path}/cd_builder/:section?`} component={CDBuilderPage} />
            <Route path={`${path}/comments`} component={CommentsRouter} />
            <Route path={`${path}/contacts`} exact component={OrderContactsContainer} />
            <Route path={`${path}/documents`} exact component={DocumentsPage} />
            <Route path={`${path}/documents/:documentId/versions`} exact component={DocumentHistoryContainer} />
            <Route path={`${path}/escrow`} exact component={SpaOrderEscrowViewContainer} />
            <Route path={`${path}/escrow/edit`} exact component={SpaOrderEscrowCreateEditContainer} />
            <Route path={`${path}/fees/:section?/:feeId?`} exact component={FeesPage} />
            <Route path={`${path}/scheduling/:appointmentId?`} exact component={SchedulingContainer} />
            <Route path={`${path}/tasks/:taskId?`} exact component={TasksPage} />
            <Route path={`${path}/title`} exact component={TitleContainer} />
            {isOrderWorkflowEnabled && <Route path={`${path}/workflows`} exact component={WorkflowContainer} />}
            {isMessagingEnabled && <Route path={`${path}/inbox`} exact component={InboxPage} />}
          </OrderLayout>
        </Switch>
      </ApplicationProvider>
    </OrderLoadingGuard>
  );
};
